import React, { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { setEditDialog } from '../../redux/reducer/edit-dialog';
import { editOrderApi, editPositionApi } from '../../api/trade';
import { Notification } from '../../utilities/alert';
import { getAccountTradeId } from '../../config';
import { SubSettingContext } from '../SubscribeSettingProvider';
import emitter from '../../utilities/emitter';
import useGetLastTick from '../../data/useLastTick';
import { useAppDispatch, useAppSelector } from '../../redux/app';
import UseStopLossTakeProfit from '../../utilities/useStopLossTakeProfit';
import { setPayload } from '../../redux/reducer/payload';
import InputPrice from '../trading-dialog/components/InputPrice';
import InputTriggerPrice from '../trading-dialog/components/InputTriggerPrice';
import { type } from '../../constants';
import ExpirationDate from '../trading-dialog/components/ExpirationDate';
import moment from 'moment';
import { makeInstrumentInfoSelector } from '../../redux/selector';
import { updateOrder } from '../../redux/reducer/orders';
import { EXPIRATION } from '../../constants/expireation';
import { Button } from '@vt/vt-ui';
const EditPosition = memo(() => {
  const edit = useSelector((state) => state.editDialog);
  const { onSubscribe, unSubscribe } = useContext(SubSettingContext);
  const slref = useRef(null);
  const tpRef = useRef(null);
  const [open, setOpen] = useState(false);

  const getInstrumentInfo = useMemo(makeInstrumentInfoSelector, []);
  const instrumentInfo = useAppSelector((state) => getInstrumentInfo(state, edit?.data.symbol));

  const dispatch = useAppDispatch();
  const payload = useAppSelector((state) => state.payload?.payload);
  const { handlePrice } = UseStopLossTakeProfit();

  const { active } = useSelector(({ symbol }) => symbol);
  const [loading, setLoading] = useState(false);

  const { getLastTick } = useGetLastTick();

  const [bidAsk, setBidAsk] = useState({
    bid: 0,
    ask: 0,
    symbol: ''
  });

  const [error, setError] = useState(false);

  useEffect(() => {
    if (edit?.data) {
      getLastTick(edit.data.symbol).then(() => {
        onSubscribe(edit.data.symbol);
      });
      let payloadData = {
        price_sl: edit.data.priceSL,
        price_tp: edit.data.priceTP
      };
      if (edit?.type === 'editLimit') {
        payloadData.price_order = edit.data.priceOrder;
      }
      if (edit?.type === 'editStopLimit') {
        payloadData.price_order = edit.data.priceOrder;
        payloadData.price_trigger = edit.data.priceTrigger;
      }
      dispatch(
        setPayload({
          ...payload,
          ...payloadData
        })
      );
    }
  }, [edit]);

  const onClose = (symbol) => {
    if (symbol !== active) {
      unSubscribe(edit?.data?.symbol);
    }
    dispatch(
      setPayload({
        ...payload,
        time_expiration: 0,
        type_time: 0,
        price_order: 0,
        price_trigger: 0,
        price_sl: 0,
        price_tp: 0
      })
    );
  };

  useEffect(() => {
    emitter.instance.on('TickData', (event) => {
      if (event[edit?.data.symbol]) {
        setBidAsk({
          symbol: edit?.data.symbol,
          bid: event[edit?.data.symbol].bid,
          ask: event[edit?.data.symbol].ask
        });
      }
    });
  }, [edit]);

  const onEdit = async (payload, type) => {
    try {
      let payloadData = {
        account_trade_id: getAccountTradeId(),
        ticket: edit?.data.position,
        price_order: payload.price_order,
        price_sl: payload.price_sl,
        price_tp: payload.price_tp,
        price_trigger: payload.price_trigger ?? 0,
        type_time: payload.type_time
      };

      if (payload.time_expiration <= 0)
        payloadData.time_expiration = moment.tz('Europe/London').isDST()
          ? moment().utc().unix() + 3600
          : moment().utc().unix();
      else payloadData.time_expiration = payload.time_expiration;
      if (type === 'edit') payloadData.symbol = edit.data.symbol;
      if (type === 'buy' || type === 'sell') await editPositionApi(payloadData);
      if (type.startsWith('edit')) await editOrderApi(payloadData);

      Notification({ title: 'Success', type: 'success', message: 'Edit Success' });
      dispatch(setEditDialog({ isOpen: false, type: '', data: null }));
      dispatch(setPayload({
        ...payload,
        price_sl: 0,
        price_tp: 0,
      }))
      dispatch(
        updateOrder({
          positionId: edit?.data.position,
          data: {
            priceOrder: payloadData.price_order,
            priceTrigger: payloadData.price_trigger,
            priceSL: payloadData.price_sl,
            priceTP: payloadData.price_tp,
            time_expiration: payloadData.time_expiration,
            type_time: payloadData.type_time
          }
        })
      );
      setLoading(false);
    } catch (e) {
      setLoading(false);
      Notification({
        title: 'Invalid',
        type: 'danger',
        message: e?.response?.data?.error?.message
      });
    }
  };

  const typeEdit = (type) => {
    let a = type;
    if (
      a === 'editBuyStop' ||
      a === 'editSellStop' ||
      a === 'editBuyLimit' ||
      a === 'editSellLimit'
    ) {
      a = 'edit';
    } else if (a === 'editBuyStopLimit' || a === 'editSellStopLimit') {
      a = 'editStopLimit';
    }

    switch (a) {
      case 'edit':
        return (
          <div className="px-[20px]">
            <div className="text-[#e5e5e5] text-sm">Price</div>
            <div className="mt-2">
              <InputPrice
                validate={edit?.type === 'editLimit' ? 2 : 3}
                className="rounded-lg"
                editBidAsk={{ ...bidAsk, digit: edit?.data?.digits ?? 0 }}
              />
            </div>
          </div>
        );
      case 'editStopLimit':
        return (
          <>
            <div className="text-[#e5e5e5] text-sm px-[20px]">Price</div>
            <div className="px-[10px]">
              <InputPrice
                validate={edit?.type === 'editStopLimit' ? 2 : 3}
                editBidAsk={{ ...bidAsk, digit: edit?.data?.digits ?? 0 }}
                className="rounded-lg mt-0"
              />
            </div>
            <div className="text-[#e5e5e5] text-sm px-[20px] mt-4">Stop Limit Price</div>
            <div className="px-[10px]">
              <InputTriggerPrice
                validate={edit?.type === 'editStopLimit' ? 6 : 7}
                className="rounded-lg mt-0"
              />
            </div>
          </>
        );
      default:
        break;
    }
  };

  const editTitle = (type) => {
    let isType = type;
    if (type !== 'buy' && type !== 'sell') isType = 'order';

    switch (isType) {
      case 'order':
        return 'Order';
      default:
        return 'Position';
    }
  };

  useEffect(() => {
    if (slref.current) {
      if (typeof payload.price_sl === 'string') {
        slref.current.value = payload.price_sl;
      } else {
        slref.current.value = payload.price_sl <= 0 ? '' : payload.price_sl;
      }
    }
    if (tpRef.current) {
      if (typeof payload.price_tp === 'string') {
        tpRef.current.value = payload.price_tp;
      } else {
        tpRef.current.value = payload.price_tp <= 0 ? '' : payload.price_tp;
      }
    }
  }, [payload.price_sl, payload.price_tp]);

  const handleChageExpiration = (value) => {
    switch (value) {
      case 0:
        dispatch(setPayload({ ...payload, type_time: value, time_expiration: 0 }));
        break;
      case 1:
        dispatch(
          setPayload({
            ...payload,
            type_time: value,
            time_expiration: moment.tz('Europe/London').isDST()
              ? moment().utc().unix() + 3600
              : moment().utc().unix()
          })
        );
        break;
      case 2:
        dispatch(
          setPayload({
            ...payload,
            type_time: value,
            time_expiration: moment.tz('Europe/London').isDST()
              ? moment().utc().unix() + 3600
              : moment().utc().unix()
          })
        );
        break;
      case 3:
        dispatch(
          setPayload({
            ...payload,
            type_time: value,
            time_expiration: moment.tz('Europe/London').isDST()
              ? moment().utc().unix() + 3600
              : moment().utc().unix()
          })
        );
        break;
      default:
        break;
    }
  };

  return (
    <div
      className="backdrop"
      style={{
        transition: 'all 0.3s'
      }}>
      <div
        id="edit-position"
        className="max-w-[500px] max-h-max m-auto bg-[#151515] rounded-[20px] border border-[#232323]">
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 20px 0px 20px',
              alignItems: 'center'
            }}>
            <div></div>
            <div className="text-center text-[#e5e5e5] text-sm" style={{ fontWeight: 'bold' }}>
              {`${editTitle(edit?.type)}: #${edit?.data?.position} ${type.str[edit?.type]} ${((edit?.data?.volume / 100) * 1) / 100
                } ${edit?.data?.symbol} ${edit?.data?.priceOrder?.toFixed(edit?.data.digits) ??
                edit?.data?.priceOpen?.toFixed(edit?.data.digits)
                }`}
            </div>
            <div>
              <button
                className="defult"
                onClick={() => {
                  dispatch(setEditDialog({ isOpen: false, type: null, data: null }));
                  onClose(edit?.data?.symbol);
                }}>
                <svg
                  width="23"
                  height="23"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                  <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                  <g id="SVGRepo_iconCarrier">
                    <path
                      d="M16 8L8 16M8 8L16 16"
                      stroke="#9f9f9f"
                      strokeWidth="2"
                      strokeLinecap="round"></path>{' '}
                  </g>
                </svg>
              </button>
            </div>
          </div>
          <hr className="border border-[#393939] my-3" />
          <div className="edit-position mt-2 px-[20px]">
            <p className='text-[#e5e5e5]'>Symbol</p>
            <input disabled className="symbol-info text-sm" defaultValue={`${edit?.data?.symbol}`} />
            <div style={{ height: '10px' }} />
            <p className='text-[#e5e5e5]'>Type</p>
            <input
              disabled
              className="symbol-info text-sm"
              value={!edit?.type.startsWith('edit') ? 'Modify Position' : 'Modify Order'}
            />
          </div>
          {edit?.type.startsWith('edit') && <div className="h-[10px]" />}
          {typeEdit(edit?.type)}
          <div className="h-[10px]" />
          <div className="flex">
            <div className="edit_position_T">
              <p className="px-[20px] text-[#e5e5e5]">Stop Loss</p>
              <div className="box-input-lotsize">
                <div>
                  <button
                    disabled={+payload.price_sl === 0}
                    className="dec"
                    onClick={() =>
                      handlePrice(
                        'dec',
                        payload.price_sl,
                        'price_sl',
                        edit?.data?.digits,
                        dispatch,
                        payload,
                        bidAsk.bid
                      )
                    }>
                    -
                  </button>
                </div>
                <input
                  className={`edit_position_T input-left-lotsize ${payload.price_sl !== 0 ? (error ? 'errors' : '') : ''
                    }`}
                  placeholder="0000.00"
                  type="number"
                  ref={slref}
                  onChange={({ target: { value } }) => {
                    dispatch(
                      setPayload({
                        ...payload,
                        price_sl: value
                      })
                    );
                  }}
                  defaultValue={payload.price_sl}
                  onMouseDown={() => {
                    if (payload.price_sl === 0 || payload.price_sl === '') {
                      dispatch(
                        setPayload({
                          ...payload,
                          price_sl: bidAsk.bid
                        })
                      );
                    }
                  }}
                />
                <div>
                  <button
                    disabled={+payload.price_sl === 0}
                    className="inc"
                    onClick={() =>
                      handlePrice(
                        'inc',
                        payload.price_sl,
                        'price_sl',
                        edit?.data?.digits,
                        dispatch,
                        payload,
                        bidAsk.bid
                      )
                    }>
                    +
                  </button>
                </div>
              </div>
            </div>
            {/* ============ */}
            <div className="edit_position_T">
              <p style={{ padding: '0 20px' }} className='text-[#e5e5e5]'>Take Profit</p>
              <div className="box-input-lotsize">
                <div>
                  <button
                    disabled={+payload.price_tp === 0}
                    className="dec"
                    onClick={() =>
                      handlePrice(
                        'dec',
                        payload.price_tp,
                        'price_tp',
                        edit?.data?.digits,
                        dispatch,
                        payload,
                        bidAsk.bid
                      )
                    }>
                    -
                  </button>
                </div>
                <input
                  className={`edit_position_T input-left-lotsize ${+payload?.price_tp !== 0 ? (error ? 'errors' : '') : ''
                    }`}
                  placeholder="0000.00"
                  type="number"
                  ref={tpRef}
                  onChange={({ target: { value } }) => {
                    dispatch(
                      setPayload({
                        ...payload,
                        price_tp: value
                      })
                    );
                  }}
                  defaultValue={payload.price_tp}
                  onMouseDown={() => {
                    if (payload.price_tp === 0 || payload.price_tp === '') {
                      dispatch(
                        setPayload({
                          ...payload,
                          price_tp: bidAsk.ask.toFixed(edit?.data.digits)
                        })
                      );
                    }
                  }}
                />
                <div>
                  <button
                    disabled={+payload.price_tp === 0}
                    className="inc"
                    onClick={() =>
                      handlePrice(
                        'inc',
                        payload.price_tp,
                        'price_tp',
                        edit?.data?.digits,
                        dispatch,
                        payload,
                        bidAsk.bid
                      )
                    }>
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>
          {edit?.type.startsWith('edit') && (
            <div className="px-[20px] mt-2">
              <div>
                <div className="text-[#e5e5e5] mb-1 text-[13px]">Expiration:</div>
                <div
                  className="border rounded-lg flex-1"
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = '#0FEF9E'
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = open ? '#0FEF9E' : '#4B4B4B'
                  }}
                  style={{
                    borderColor: open ? '#0FEF9E' : '#4B4B4B',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderRadius: '8px'
                  }}
                >
                  <div className="flex flex-col px-3 relative w-full">
                    <div
                      className="flex justify-between items-center cursor-pointer h-[40px]"
                      onClick={() => setOpen(!open)}>
                      <div className="text-[#9f9f9f] text-sm">
                        {EXPIRATION.find((e) => e.value === payload.type_time)?.label}
                      </div>
                      <div>
                        <svg
                          className={`transition-all ${open ? 'rotate-0' : 'rotate-180'
                            } history-btn-action`}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                          <g id="SVGRepo_iconCarrier">
                            {' '}
                            <path
                              d="M18.2929 15.2893C18.6834 14.8988 18.6834 14.2656 18.2929 13.8751L13.4007 8.98766C12.6195 8.20726 11.3537 8.20757 10.5729 8.98835L5.68257 13.8787C5.29205 14.2692 5.29205 14.9024 5.68257 15.2929C6.0731 15.6835 6.70626 15.6835 7.09679 15.2929L11.2824 11.1073C11.673 10.7168 12.3061 10.7168 12.6966 11.1073L16.8787 15.2893C17.2692 15.6798 17.9024 15.6798 18.2929 15.2893Z"
                              fill="#9f9f9f"></path>
                            {' '}
                          </g>
                        </svg>
                      </div>
                    </div>
                    {open && (
                      <div
                        onMouseEnter={() => setOpen(true)}
                        onMouseLeave={() => setOpen(false)}
                        className="absolute z-10 top-[48px] left-0 bg-[#202020] border border-[#80808059] w-full flex flex-col rounded-md">
                        {EXPIRATION.map((e, i) => (
                          <div
                            onClick={() => {
                              setOpen(false);
                              handleChageExpiration(e.value);
                            }}
                            key={e.value}
                            className="text-[#e5e5e5] text-sm px-4 h-[30px] flex items-center hover:bg-[#404040] transition-all cursor-pointer">
                            {e.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {payload.type_time >= 2 ? (
                <ExpirationDate
                  className="px-0 rounded-lg mt-2"
                  labelClass="mt-2 text-[13px]"
                  inputClass="rounded-lg"
                  iconClass="top-4"
                />
              ) : null}
            </div>
          )}
          <div className="exit_price_bid_ask p-[20px]">
            <div className="bid-edit !text-[#e5e5e5]">{bidAsk?.bid.toFixed(edit?.data.digits)}</div>
            <div className="center !text-[#e5e5e5]">/</div>
            <div className="ask !text-[#e5e5e5]">{bidAsk?.ask.toFixed(edit?.data.digits)}</div>
          </div>
          <div style={{ padding: '0px 20px 20px', display: 'flex', justifyContent: 'center' }}>
            <Button className="w-full" onClick={() => {
              onEdit(payload, edit?.type);
            }}
              disabled={loading}>
              {`Modify #${edit?.data.position} buy ${((edit?.data?.volume / 100) * 1) / 100}
                ${edit?.data?.symbol} ${edit?.data?.priceOrder?.toFixed(edit?.data.digits) ??
                edit?.data?.priceOpen?.toFixed(edit?.data.digits)
                }`}
            </Button>
          </div>
          <div className="modify_text">
            <p className='text-[#e5e5e5]'>Stop Loss or Take Profit levels must differ from the market price by at least{' '}</p>
            {instrumentInfo.stops_level ?? 0} points. Stop processing is performed on the
            <p className='text-[#e5e5e5]'>broker&apos;s side.</p>
          </div>
        </>
      </div>
    </div>
  );
});

EditPosition.displayName = 'EditPosition';

export default EditPosition;
